// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React /* eslint-disable-line no-unused-vars */, {
  useState,
} from 'react';

import { Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';

import TrialWelcomeModalDefaultView from './TrialWelcomeModalDefaultView';
import TrialWelcomeModalIndexLink from './TrialWelcomeModalIndexLink';
import TrialWelcomeModalIndexView from './TrialWelcomeModalIndexView';
import TrialWelcomeModalTopicView from './TrialWelcomeModalTopicView';

import { type Size } from '@zapier/design-system-company';

/** @jsx isolateComponent */

type Props = {
  /** The index of the view to show when the modal first opens */
  initialView?: number,
  /** Callback to be executed when the modal is closed */
  onClose: () => void,
  /** Size */
  size: Size,
};

const topics = [
  {
    title: 'Create unlimited teams',
    body:
      "Invite as many team members as you want—they're all included in your plan. It's the best tool for your company to automate their work and grow your business.",
    imageAlt: 'Team creation workflow',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/a944491ba071472939721827a273348a.png',
  },
  {
    title: 'Set up single-sign on with SAML',
    body:
      'Integrate Zapier with your existing SAML 2.0 identity and access management (IAM) provider to ensure secure access to your team. Built-in connections to popular SSO providers and an option for using your own custom SAML solution.',
    imageAlt: 'SSO sign-in box',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/2fd066948f41b439e756c83260471937.png',
  },
  {
    title: 'Manage domain insights and account capture in Zapier',
    body:
      'Team admins can see who has created Zapier accounts using a work email address on their verified domain. Account capture helps you make sure your entire company is using a single account. Make sure your security policies are enforced company wide.',
    imageAlt: 'Settings view for company domain',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/4cd668dc7bdab831842004424f4f2051.png',
  },
  {
    title: 'Provision user accounts with SCIM',
    body:
      'Manage account membership with your identity and access management (IAM) provider to ensure the right people have access. Works with any identity provider that support the SCIM protocol.',
    imageAlt: 'Three icons connected by dotted arrows',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/60caac8a093336aa50d8271d3bdcf9da.png',
  },
  {
    title: 'Customize task history retention in Zapier',
    body:
      "Customize your task history retention to match your company's legal and regulatory requirements. Or extend your task history up to 90 days to help with troubleshooting.",
    imageAlt: 'Data retention period slider',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/44fae625fde2ad42c32cfee55c151481.png',
  },
  {
    title: 'Manage access to apps in Zapier',
    body:
      "You decide which apps can be connected to Zapier. Don't want any data from your CRM connecting to Zapier, no problem. With Access Control for Apps you have control over what data is connected to Zapier.",
    imageAlt: 'Settings view for app permissions',
    imageSrc:
      'https://cdn.zapier.com/storage/photos/6bb77764e265f42696471970b03491fe.png',
  },
];

/**
 * Shown to the user when they start a new companies trial.
 */
const TrialWelcomeModal = (props: Props) => {
  const [activeView, setActiveView] = useState(props.initialView || 0);
  const goToIndexView = () => setActiveView(1);

  const views = [
    <TrialWelcomeModalDefaultView
      onButtonClick={goToIndexView}
      size={props.size}
    />,

    <TrialWelcomeModalIndexView size={props.size}>
      {topics.map((item, index) => {
        return (
          <TrialWelcomeModalIndexLink
            key={index}
            onClick={() => setActiveView(index + 2)} // +2 because default page is 0 and index page is 1
            text={item.title}
          />
        );
      })}
    </TrialWelcomeModalIndexView>,

    ...topics.map((item, index) => {
      return (
        <TrialWelcomeModalTopicView
          imageAlt={item.imageAlt}
          imageSrc={item.imageSrc}
          key={index}
          onButtonClick={goToIndexView}
          size={props.size}
          title={item.title}
        >
          {item.body}
        </TrialWelcomeModalTopicView>
      );
    }),
  ];

  return (
    <Modal
      closeOnOutsideClick={true}
      maxWidth={600}
      onClose={props.onClose}
      overflowY="auto"
      padding={activeView === 0 ? 0 : 40}
    >
      {views[activeView]}
    </Modal>
  );
};

export default TrialWelcomeModal;
